<template>
  <div class="layout-fullscreen layout-fullscreen--no-header">
    <PageHeaderBar :title="$t('nav.definition')" />
    <EmptyMessage
      class="hide-for-medium"
      :message="$t('regions.unsupported_device_size_message')"
      :title="$t('regions.unsupported_device_size')"
    >
      <ExclamationOctagonSvg />
    </EmptyMessage>
    <div class="definition-layout show-for-medium">
      <div
        ref="sceneContainer"
        class="scene-container"
      >
        <Shaft3dModelOrientation />
        <Shaft3dModelToolbar
          v-model="toolbarValue"
          :showSidewallCheckbox="hasSidewalls"
          :sidewallsChecked="sideWallsActive"
          @display-sidewalls="displaySidewalls"
          @update:model-value="selectedAction"
        />
        <div
          v-if="elementHoverName"
          class="scene-container__hover-name"
        >
          {{ elementHoverName }}
        </div>
      </div>
      <aside class="scroll-container">
        <p
          v-if="!showForm"
          class="definition-layout__select-state"
        >
          <strong>{{ $t('shafts.definition.set_up_element') }}</strong>
        </p>
        <p
          v-if="showForm"
          class="definition-layout__select-state"
        >
          <strong>{{ addOrEditText }}
            {{ $t(`shafts.element_types.type_singular.${currentSelectedElement?.userData?.type}`).toLowerCase() }}
          </strong>
        </p>
        <!-- SELECT -->
        <div
          v-if="!showForm"
          :aria-label="$t('shafts.definition.add_new_element')"
          class="dropdown"
        >
          <Popper
            v-model:open="newElementOpen"
            :modifiers="['side-panel']"
            placement="bottom-start"
          >
            <template #trigger>
              <div class="dropdown__trigger">
                <div class="select__trigger issue-ratings__drop-trigger">
                  <button
                    class="button button--full-width button--space-between"
                    type="button"
                  >
                    <div class="button__inner-wrapper">
                      <Plus16Svg />
                      &nbsp;{{ $t('shafts.definition.add_new_element') }}&nbsp;
                    </div>
                    <div
                      :class="{
                        'select__arrow select__arrow--white select__arrow--medium': true,
                        'select__arrow--active': newElementOpen,
                      }"
                    >
                      <ArrowDown16Svg />
                    </div>
                  </button>
                </div>
              </div>
            </template>

            <div
              v-if="!newElement"
              class="menu menu--dropdown"
            >
              <ul class="menu__items">
                <li
                  v-for="(element, index) in elementTypes"
                  :key="index"
                  class="menu-item menu-item--dropdown"
                >
                  <button
                    class="menu-item__link"
                    type="button"
                    @click="addElement(element.name)"
                  >
                    <span class="menu-item__label">
                      {{ $t(`shafts.element_types.type_singular.${element.name}`) }}
                    </span>
                  </button>
                </li>
              </ul>
            </div>
          </Popper>
        </div>

        <div
          v-if="!showForm"
          class="element-list"
        >
          <div
            v-if="filteredElementGroups"
            class="element-list__filter"
          >
            <p class="typo-ignore typo-small-title">
              {{ $t('shafts.definition.existing_elements') }}
            </p>
            <Select
              v-model="filterValue"
              :items="filterOptions"
              :labelLengthLimit="150"
              @update:model-value="filterElementGroups"
            />
          </div>
          <div
            v-for="group in filteredElementGroups"
            :key="group.id"
            class="element-list__group"
          >
            <template v-if="group.shaft_elements.length > 0">
              <div class="group">
                <p class="element-list__group-title typo-ignore typo-tiny">
                  {{ group.name }}
                </p>
                <div class="element-list__group-section">
                  <div
                    v-for="element in group.shaft_elements"
                    :key="element.id"
                    class="element-list__group-section-items"
                    :data-hover="listHoverState"
                    @mouseenter="highlightOnHover(element.id)"
                    @mouseleave="resetHighlightHover"
                  >
                    <div>{{ element.name }}</div>
                    <div class="element-list__group-section-items-actions">
                      <button
                        :aria-label="$t('shafts.definition.delete_element')"
                        class="button"
                        type="button"
                        @click="selectElementToDelete(element)"
                      >
                        <TrashIcon class="icon" />
                      </button>
                      <button
                        :aria-label="$t('shafts.definition.edit_element')"
                        class="button"
                        type="button"
                        @click="editElement(element)"
                      >
                        <PencilIcon class="icon" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
          <div v-if="existingElements && existingElements.length === 0">
            {{ $t('shafts.definition.no_existing_elements') }}
          </div>
        </div>

        <div
          v-if="showForm"
          class="form-nav"
        >
          <FormRow
            :errors="errors?.shaft_element?.name"
            :label="`${$t(`shafts.element_types.type_singular.${currentSelectedElement?.userData?.type.toLowerCase()}`)} ${$t('general.name')}`"
            labelFor="element"
            :modifiers="['no-padding']"
          >
            <div class="input">
              <input
                :id="`${currentSelectedElement.userData.type} name`"
                v-model="currentlyEditing.name"
                :aria-label="$t('shafts.definition.element_name')"
                :class="{
                  input: true,
                  'input--error': errors?.shaft_element?.name,
                }"
                @input="validateInput('shaft_element', 'name')"
              >
            </div>
          </FormRow>
          <DefinitionFormSection>
            <div class="form-layout__row--mobile form-layout__row--inline">
              <div class="form-layout__column">
                <FormRow
                  :errors="errors?.shaft_element?.start_set"
                  :label="$t('shafts.group.set.starting_set')"
                  labelFor="shaft_element_start_set"
                  :modifiers="['no-padding']"
                >
                  <div class="form-row__input form-row__input--no-peek">
                    <div
                      :class="{
                        input: true,
                        'input--error': errors?.shaft_element?.start_set,
                      }"
                    >
                      <input
                        id="shaft_element_start_set"
                        v-model="currentlyEditing.start_set"
                        :placeholder="$t('shafts.group.set.input_set')"
                        @blur="!startSetWasTouched && validateInput('start_set', 'number')"
                      >
                    </div>
                  </div>
                </FormRow>
              </div>

              <div class="form-layout__column">
                <FormRow
                  :errors="errors?.shaft_element?.end_set"
                  :label="$t('shafts.group.set.end_set')"
                  labelFor="shaft_element_end_set"
                  :modifiers="['no-padding']"
                >
                  <div class="form-row__input form-row__input--no-peek">
                    <div
                      :class="{
                        input: true,
                        'input--error': errors?.shaft_element?.end_set,
                      }"
                    >
                      <input
                        id="shaft_element_end_set"
                        v-model="currentlyEditing.end_set"
                        :placeholder="$t('shafts.group.set.input_set')"
                        @blur="!endSetWasTouched && validateInput('end_set', 'number')"
                      >
                    </div>
                  </div>
                </FormRow>
              </div>
            </div>
          </DefinitionFormSection>
          <DefinitionFormSection v-if="hasType && typeOptions.length > 0">
            <FormRow
              :errors="errors?.shaft_element?.type"
              :label="$t('shafts.group.type.label')"
              :modifiers="['no-padding', 'margin-bottom']"
            >
              <div class="form-row__input form-row__input--no-peek">
                <SelectList
                  v-model="selectedMemberType"
                  :class="{
                    input: true,
                    'input--error': errors?.shaft_element?.type,
                  }"
                  :labelGroup="labelTypeGroup"
                  :list="typeOptions"
                  :modifiers="['side-panel-small']"
                  :placeholderAsValue="false"
                />
              </div>
            </FormRow>
          </DefinitionFormSection>

          <DefinitionFormSection
            v-if="!isCircleSideWall(currentlyEditing.userData.type)"
            :title="$t('shafts.definition.dimension')"
          >
            <DefinitionFormInput
              v-model="currentlyEditing.scale.x"
              :data="{
                id: widthOrDiameter.id,
                label: widthOrDiameter.label,
                maxValue: isCircle ? shaft.dimensions.diameter : shaft.dimensions.width,
                minValue: 0,
                step: 0.1,
              }"
              @input="updateElement"
            />
            <DefinitionFormInput
              v-if="!isConveyance(currentlyEditing.userData.type)"
              v-model="currentlyEditing.scale.y"
              :data="{
                id: 'height',
                label: $t('general.height'),
                maxValue: shaft.set_spacing,
                minValue: 0,
                step: 0.1,
              }"
              @input="updateElement('height')"
            />
            <DefinitionFormInput
              v-if="!isCylinderMesh(currentlyEditing.userData.type)"
              v-model="currentlyEditing.scale.z"
              :data="{
                id: 'length',
                label: $t('general.length'),
                maxValue: isCircle ? shaft.dimensions.diameter : shaft.dimensions.length,
                minValue: 0,
                step: 0.1,
              }"
              @input="updateElement"
            />
          </DefinitionFormSection>

          <DefinitionFormSection
            v-if="!isCircleSideWall(currentlyEditing.userData.type)"
            :title="$t('shafts.definition.position')"
          >
            <DefinitionFormInput
              v-model="currentlyEditing.position.x"
              :data="{
                id: 'x',
                label: $t('general.west_east'),
                secondaryLabel: $t('general.x_axis'),
                maxValue: maxMinValue(shaft.dimensions.diameter || shaft.dimensions.width),
                minValue: -maxMinValue(shaft.dimensions.diameter || shaft.dimensions.width),
                step: 0.1,
              }"
              @input="updateElement"
            />
            <DefinitionFormInput
              v-model="currentlyEditing.position.z"
              :data="{
                id: 'z',
                label: $t('general.north_south'),
                secondaryLabel: $t('general.z_axis'),
                maxValue: maxMinValue(shaft.dimensions.diameter || shaft.dimensions.length),
                minValue: -maxMinValue(shaft.dimensions.diameter || shaft.dimensions.length),
                step: 0.1,
              }"
              @input="updateElement"
            />
            <DefinitionFormInput
              v-if="!isConveyance(currentlyEditing.userData.type)"
              v-model="currentlyEditing.position.y"
              :data="{
                id: 'y',
                label: $t('general.up_down'),
                secondaryLabel: $t('general.y_axis'),
                maxValue: shaft.set_spacing,
                minValue: 0,
                step: 0.1,
              }"
              @input="updateElement('position')"
            />
          </DefinitionFormSection>
          <DefinitionFormSection
            v-if="isCircleSideWall(currentlyEditing.userData.type)"
            :title="$t('shafts.definition.position')"
          >
            <DefinitionFormInput
              v-model="currentlyEditing.position.thetaStart"
              :data="{
                id: 'start',
                label: $t('general.sidewall.start'),
                maxValue: 6.283,
                minValue: 0.001,
                step: 0.001,
              }"
              @input="updateSidewall(currentlyEditing)"
            />
            <DefinitionFormInput
              v-model="currentlyEditing.position.thetaLength"
              :data="{
                id: 'end',
                label: $t('general.sidewall.end'),
                maxValue: 6.283,
                minValue: 0.001,
                step: 0.001,
              }"
              @input="updateSidewall(currentlyEditing)"
            />
          </DefinitionFormSection>
          <DefinitionFormSection>
            <FormRow
              :label="$t('shafts.group.assign_to_group')"
              :modifiers="['no-padding']"
            >
              <div class="form-row__input form-row__input--no-peek">
                <SelectList
                  v-model="selectedGroup"
                  :labelGroup="labelGroup"
                  :list="elementGroups"
                  :modifiers="['side-panel-small']"
                />
              </div>
            </FormRow>
            <FormRow
              v-if="selectedGroup && selectedGroup.isNew"
              :errors="errors?.shaft_element_group?.name"
              :label="$t('shafts.group.add_new_group')"
              labelFor="new_group_name"
              :modifiers="['no-padding']"
            >
              <div class="form-row__input form-row__input--no-peek">
                <div
                  :class="{
                    input: true,
                    'input--error': errors?.shaft_element_group?.name,
                  }"
                >
                  <input
                    id="new_group_name"
                    v-model="selectedGroup.name"
                    @input="validateInput('shaft_element_group', 'name')"
                  >
                </div>
              </div>
            </FormRow>
          </DefinitionFormSection>
          <!-- BUTTONS -->
          <div class="definition-layout__form-actions">
            <button
              class="button"
              type="button"
              @click="cancelEdit"
            >
              {{ $t('general.cancel') }}
            </button>
            <FormSubmitButton
              :isSaving="saving"
              @click="saveElement"
            />
          </div>
        </div>
      </aside>
    </div>
  </div>
  <Dialog
    v-if="dialogIsOpen"
    :message="$t('shafts.definition.delete_confirm_message')"
    :title="$t('shafts.definition.delete_confirm_title')"
    @dismiss-dialog="dismissDialog"
  >
    <template #actions>
      <button
        class="button button--inverse"
        type="button"
        @click="handleDelete"
      >
        {{ $t('general.delete') }}
      </button>
    </template>
  </Dialog>
</template>

<script setup>
import { onMounted, ref, computed, watch } from 'vue';
import { ShaftElement, setSideWallGeometry } from '@/functions/shaft3dModel';
import { useShaft3dModel } from '@/use/useShaft3dModel';
import useAjax from '@/functions/use_ajax';
import { convertToUnderscore } from '@/functions/convert_to_underscore';
import diameterOrDiagonal from '@/functions/shaft3dExtra';
import I18n from 'i18n-js';

// Components
import DefinitionFormInput from '@/components/shaftsV2/definition/definition_form_input.vue';
import DefinitionFormSection from '@/components/shaftsV2/definition/definition_form_section.vue';
import EmptyMessage from '@/components/generic/empty_message.vue';
import FormRow from '@/components/form/row.vue';
import FormSubmitButton from '@/components/form/form_submit_button.vue';
import PageHeaderBar from '@/components/generic/page_header_bar.vue';
import Popper from '@/components/generic/popper.vue';
import SelectList from '@/components/shaftsV2/operations/select_list.vue';
import Dialog from '@/components/generic/dialog.vue';
import Select from '@/components/form/select.vue';
import Shaft3dModelOrientation from '@/components/shaftsV2/shaft3dModel/shaft_3d_model_orientation.vue';
import Shaft3dModelToolbar from '@/components/shaftsV2/shaft3dModel/shaft_3d_model_toolbar.vue';

// Icons
import ExclamationOctagonSvg from '@/images/exclamation_octogon.svg';
import Plus16Svg from '@/images/plus-16.svg';
import ArrowDown16Svg from '@/images/arrow-down-16.svg';
import TrashIcon from '@/images/trash-24.svg';
import PencilIcon from '@/images/pencil-24.svg';

const {
  initTHREE,
  SCENE,
  elementHoverName,
  clearScene,
  resetCamera,
  getAllElements,
  setElementMaterialType,
  applyConveyanceStyle,
  displaySidewalls,
  flattenElements,
  sideWallsActive
} = useShaft3dModel();

const props = defineProps({
  organisationId: { type: String, required: true },
  shaftId: { type: String, default: null },
});

const ajax = useAjax();
const errors = ref(null);
const loading = ref(true);
const newElement = ref(null);
const newElementOpen = ref(false);
const saving = ref(false);
const shaft = ref(null);
const dialogIsOpen = ref(false);
const deletionId = ref(null);
const listHoverState = ref(false);

const isEditingState = ref(false);

const elementTypes = ref(null);
const selectedElementType = ref(null);
const sceneContainer = ref();

const activeElement = ref(null);
const currentSelectedElement = ref(null);
const existingElements = ref([]);

const currentlyEditing = ref({});
const showForm = ref(false);
const showFormNav = ref(true);
const elementGroups = ref(null);
const filteredElementGroups = ref(null);
const filterValue = ref(null);
const selectedGroup = ref(null);
const selectedMemberType = ref(null);
const labelGroup = ref({
  newItemLabel: I18n.t('shafts.group.new_group'),
  noItemLabel: I18n.t('shafts.group.no_group'),
});
const labelTypeGroup = ref({
  noItemLabel: I18n.t('shafts.group.type.member_label'),
});
const toolbarValue = ref(null);
const typeOptions = ref(null);
const startSetWasTouched = ref(false);
const endSetWasTouched = ref(false);
const conveyanceTypeOptions = [
  'skip',
  'cage',
].map((type) => ({
  name: I18n.t(`shafts.group.type.${type}`),
  type,
}));

onMounted(async () => {
  await loadShaft();
  await loadDefinitionData();
  loadShaftElementTypes();
});

function filterElementGroups(value) {
  if (value === 'all_elements') {
    filteredElementGroups.value = elementGroups.value;
  } else {
    filteredElementGroups.value = elementGroups.value?.filter((group) => convertToUnderscore(group.name) === value );
  }
}

const isCircle = computed(() => {
  return shaft.value?.shape === 'circle';
});

const filterOptions = computed(() => {
  let options;

  options = elementGroups.value?.filter((group) => group?.shaft_elements.length > 0)
    .map((group) => {
      return {
        label: group.name === 'Ungrouped' ? I18n.t('shafts.definition.ungrouped') : group.name,
        value: convertToUnderscore(group.name)
      };
  });

  options?.unshift({
    label: I18n.t('shafts.definition.all_elements'),
    value: 'all_elements'
  });
  return options;
});

const hasSidewalls = computed(() => {
  return existingElements.value.some(e => e.element_type === 'sidewalls');
});

const addOrEditText = computed(() => {
  return isEditingState.value
    ? I18n.t('general.edit')
    : I18n.t('shafts.definition.add_element_type');
});

async function loadDefinitionData() {
  await loadShaftElements();
  await loadShaftGroups();
  await initTHREE(sceneContainer.value, shaft.value.dimensions);
  renderElements();
  filteredElementGroups.value = elementGroups.value;
  filterValue.value = 'all_elements';
}

function renderElements() {
  existingElements.value?.forEach((element) => {
    let aShaftElement = new ShaftElement(element, shaft.value);
    SCENE.add(aShaftElement);
  });

  displaySidewalls(false);
}

watch(selectedGroup, () => {
  validateInput('shaft_element_group', 'name');
});

watch(selectedMemberType, () => {
  validateInput('shaft_element', 'type');
});

/**
 * Listens to the start_set and end_set fields for changes,
 * runs the validations and sets errors accordingly
 */
watch(
  () => currentlyEditing.value.start_set,
  (value) => {
    if (value === undefined) return;
    startSetWasTouched.value = true;
    const testBlankAndNan = validateInputSet('start_set', 'number');
    const shaftNumberOfSets = parseFloat(shaft.value?.number_of_sets);

    !testBlankAndNan && validateSet(value, currentlyEditing.value.end_set, 'start_set');
    !testBlankAndNan &&
      validateSet(currentlyEditing.value.end_set, currentlyEditing.value.start_set, 'end_set');
    !testBlankAndNan && rangeValidation(value, shaftNumberOfSets, 'start_set');
  }
);

watch(
  () => currentlyEditing.value.end_set,
  (value) => {
    if (value === undefined) return;
    endSetWasTouched.value = true;
    const testBlankAndNan = validateInputSet('end_set', 'number');
    const shaftNumberOfSets = parseFloat(shaft.value?.number_of_sets);

    !testBlankAndNan && validateSet(value, currentlyEditing.value.start_set, 'end_set');
    !testBlankAndNan &&
      validateSet(currentlyEditing.value.start_set, currentlyEditing.value.end_set, 'start_set');
    !testBlankAndNan && rangeValidation(value, shaftNumberOfSets, 'end_set');
  }
);

function maxMinValue(baseValue) {
  return isCircle.value ? baseValue : baseValue / 2;
}

function validateSet(value, limit, key) {
  if (isNaN(currentlyEditing.value?.start_set) || isNaN(currentlyEditing.value?.end_set)) return;
  if (!currentlyEditing.value?.start_set || !currentlyEditing.value?.end_set) return;

  const newValue = parseFloat(value);
  const startSetTest =
    !!newValue && key === 'start_set' ? newValue <= limit : newValue >= limit;

  const shaftNumberOfSets = parseFloat(shaft.value?.number_of_sets);
  const startSet = parseFloat(currentlyEditing.value?.start_set);
  const endSet = parseFloat(currentlyEditing.value?.end_set);

  const setTest =
    startSet === 0 ||
    endSet === 0 ||
    startSet > shaftNumberOfSets ||
    endSet > shaftNumberOfSets;

  if (!!newValue && startSetTest && !setTest) return clearValidationMessage(key);
  const label = key === 'start_set' ? 'max_limit' : 'min_limit';
  const zeroMessage = [
    I18n.t('activerecord.errors.between_set', { type: shaftNumberOfSets }),
  ];
  const defaultMessage = [
    I18n.t(`activerecord.errors.${label}`, { type: !limit ? 1 : limit }),
  ];
  const errorMessage = setTest ? zeroMessage : defaultMessage;

  if (!setTest) return setValidationMessage(errorMessage, key);
  setValidationMessage(errorMessage, 'start_set');
  setValidationMessage(errorMessage, 'end_set');
}

function validateInput(type, field) {
  if (errors.value && errors.value[type]) {
    errors.value[type][field] = null;
  }
}

function validateInputSet(key, type) {
  const value = currentlyEditing.value[key] || currentlyEditing.value.geometry[key];
  let errorMessage;

  if (type === 'number') {
    errorMessage = [I18n.t('activerecord.errors.NAN')];
    if (isNaN(value)) return ((errors.value = {}) && setValidationMessage(errorMessage, key));

    errorMessage = [I18n.t('activerecord.errors.min_limit', { type: 1 })];
    if (value === 0) return setValidationMessage(errorMessage, key);
  }

  clearValidationMessage(key);
}

function rangeValidation(value, limit, key) {
  if (isNaN(limit) || (value <= limit && value <= Number(shaft.value.number_of_sets) && value > 0)) return;
  const message = [I18n.t('activerecord.errors.between_set', { type: limit })];
  return setValidationMessage(message, key);
}

function setValidationMessage(message, key) {
  if (errors?.value?.shaft_element)
    return (errors.value.shaft_element[key] = message);
  return (errors.value = {
    shaft_element: {
      [key]: message,
    },
  });
}

function clearValidationMessage(key) {
  !!errors?.value?.shaft_element && (errors.value.shaft_element[key] = null);
}

const hasType = computed(() => {
  return selectedElementType.value === 'pipes' || selectedElementType.value === 'skips_and_cages';
});

const ENDPOINTS = computed(() => {
  let BASE_URL = `/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}`;

  return {
    shaft: BASE_URL,
    shaftElements: `${BASE_URL}/shaft_elements`,
    shaftElement: `${BASE_URL}/shaft_elements/${currentSelectedElement.value?.userData?.id}`,
    shaftElementTypes: `/organisations/${props.organisationId}/shafts_v2/shaft_elements/element_types`,
    shaftGroups: `${BASE_URL}/shaft_element_groups`,
    get post_or_patch() {
      return isEditingState.value ? this.shaftElement : this.shaftElements;
    },
  };
});

async function loadShaft() {
  let { ok, data } = await ajax(ENDPOINTS.value.shaft);

  if (ok) {
    shaft.value = data;
  }
}

async function loadShaftElements() {
  loading.value = true;

  let { ok, data } = await ajax(ENDPOINTS.value.shaftElements);

  if (ok) {
    existingElements.value = data;
  }

  loading.value = false;
}

async function loadShaftElementTypes() {
  let { ok, data } = await ajax(ENDPOINTS.value.shaftElementTypes);

  if (ok) {
    elementTypes.value = data;
  }
}

async function loadShaftGroups() {
  loading.value = true;

  let { ok, data } = await ajax(ENDPOINTS.value.shaftGroups);

  if (ok) {
    elementGroups.value = data;
  }

  loading.value = false;
}

async function saveGroupAndElement() {
  saving.value = true;

  let { ok, data } = await ajax(ENDPOINTS.value.shaftGroups, {
    method: 'POST',
    body: {
      name: selectedGroup.value.name,
      shaft_id: 'new',
      element_type: selectedElementType.value,
    },
  });

  if (ok) {
    selectedGroup.value = data;
    saveElement();
  } else {
    errors.value = data;
  }
  saving.value = false;
}

async function saveElement() {
  const testStartSet = errors.value?.shaft_element?.start_set;
  const testEndSet = errors.value?.shaft_element?.end_set;
  if (testStartSet || testEndSet) return;

  if (selectedGroup.value?.isNew) return saveGroupAndElement();
  saving.value = true;

  const currentHeight = currentlyEditing.value.userData.originalPosition.height;
  const currentPositionY = currentlyEditing.value.userData.originalPosition.y;
  const currentHeightAndPositionY = { height: currentHeight, positionY: currentPositionY };
  const currentEditHeightAndPositionY = { height: currentlyEditing.value.scale.y, positionY: currentlyEditing.value.position.y };
  const newHeightAndPositionY = toolbarValue.value === 'collapse' ? currentHeightAndPositionY : currentEditHeightAndPositionY;

  let newElement = {
    element_type: currentlyEditing.value.userData.type.toLowerCase(),
    end_set: Number(currentlyEditing.value.end_set),
    geometry: {
      width: Number(currentlyEditing.value.scale.x),
      height: Number(newHeightAndPositionY.height),
      length: Number(currentlyEditing.value.scale.z),
      type: selectedMemberType.value?.type,
    },
    name: currentlyEditing.value.name,
    position: {
      x: Number(currentlyEditing.value.position.x),
      y: Number(newHeightAndPositionY.positionY),
      z: Number(currentlyEditing.value.position.z),
      // if the element type is sidewalls add the theta data
      ...(isCircleSideWall(currentlyEditing.value.userData.type)) && {
        thetaStart: Number(currentlyEditing.value.position.thetaStart),
        thetaLength: Number(currentlyEditing.value.position.thetaLength)
      },
    },
    shaft_element_group_id: selectedGroup.value?.id || null,
    start_set: Number(currentlyEditing.value.start_set),
    symbol: '-', // TODO: add symbol
    inspectable: !isConveyance(currentlyEditing.value.userData.type)
  };

  let { ok, data } = await ajax(ENDPOINTS.value.post_or_patch, {
    method: isEditingState.value ? 'PATCH' : 'POST',
    body: {
      shaft_element: newElement,
    },
  });

  if (ok) {
    showForm.value = false;
    toolbarValue.value = 'expand';
    selectedMemberType.value = null;
    clearScene(SCENE);

    loadDefinitionData();
  } else {
    errors.value = data;
  }
  saving.value = false;
  isEditingState.value = false;
}

async function cancelEdit() {
  clearScene(SCENE);
  loadDefinitionData();

  errors.value = null;
  showForm.value = false;
  isEditingState.value = false;
  toolbarValue.value = 'expand';
  selectedMemberType.value = null;
  displaySidewalls(false);
}

/**
 * Check if element is cylindrical
 * @param {string} type - type of element.
 * @returns {boolean}
 */
function isCylinderMesh(type) {
  return ['pipes', 'cables'].includes(type);
}

/**
 * Check if element is conveyance
 * @param {string} type - type of element.
 * @returns {boolean}
 */
function isConveyance(type) {
  return type === 'skips_and_cages';
}

/**
 * Check if element is a circle sidewall
 * @param {string} type - type of element.
 * @returns {boolean}
 */
function isCircleSideWall(type) {
  return type === 'sidewalls' && isCircle.value;
}

const widthOrDiameter = computed(() => {
  if (isCylinderMesh(currentlyEditing.value.userData?.type)) {
    return { id: 'diameter', label: I18n.t('general.diameter') };
  } else {
    return { id: 'width', label: I18n.t('general.width') };
  }
});

function updateElement(type) {
  currentSelectedElement.value.name = currentlyEditing.value.name;
  if (isCylinderMesh(currentlyEditing.value.userData.type)) {
    currentSelectedElement.value.scale.x = currentlyEditing.value.scale.x;
    currentSelectedElement.value.scale.z = currentlyEditing.value.scale.x;
  } else {
    currentSelectedElement.value.scale.x = currentlyEditing.value.scale.x;
    currentSelectedElement.value.scale.y = currentlyEditing.value.scale.y;
    currentSelectedElement.value.scale.z = currentlyEditing.value.scale.z;
  }
  currentSelectedElement.value.rotation.y = currentlyEditing.value.rotation.y;
  currentSelectedElement.value.position.x = currentlyEditing.value.position.x;
  currentSelectedElement.value.position.y = currentlyEditing.value.position.y;
  currentSelectedElement.value.position.z = currentlyEditing.value.position.z;

  // Update userData height and position
  if (type !== 'height' && type !== 'position') return;
  currentlyEditing.value.userData.originalPosition.height = currentlyEditing.value.scale.y;
  currentlyEditing.value.userData.originalPosition.y = currentlyEditing.value.position.y;
}

/**
 * Update the sidewall geometry
 * @param {object} element - the element.
 */
function updateSidewall(element) {
  element.geometry.dispose();
  element.geometry = setSideWallGeometry(
    currentSelectedElement.value.position.thetaStart,
    currentSelectedElement.value.position.thetaLength
  );
}

function addElement(type) {
  showFormNav.value = false;
  newElementOpen.value = false;
  showForm.value = true;
  selectedElementType.value = type;
  selectedGroup.value = null;
  getMemberTypes(type);
  let newElementObject = {
    id: 'newElement',
    name: '',
    element_type: type,
    geometry: setElementScale(type),
    position: {
      x: 0,
      y: setElementScale(type)?.height / 2, // places the element on the shaft base level
      z: 0,
      // if the element type is sidewalls for circle
      ...(isCircleSideWall(type)) && {
        thetaStart: 0.01,
        thetaLength: 0.01,
      },
    },
    inspectable: !isConveyance(type)
  };

  const newElement = new ShaftElement(newElementObject, shaft.value);
  SCENE.add(newElement);

  currentSelectedElement.value = newElement;
  currentlyEditing.value = newElement;
  currentlyEditing.value.material?.color.set(0xe89c00);
  activeElement.value = currentSelectedElement.value;

  displaySidewalls(selectedElementType.value === 'sidewalls');
}

function setElementScale(type) {
  const scaleValue = diameterOrDiagonal(shaft.value.dimensions);
  const shaftScalePercentage = (scaleValue / 2 / 100);
  const presetData = {
    perX: shaftScalePercentage * 0.05,
    per0: shaftScalePercentage * 0.4,
    per1: shaftScalePercentage * 0.8,
    per2: shaftScalePercentage * 2,
    per3: shaftScalePercentage * 4,
    per4: shaftScalePercentage * 8,
    per5: shaftScalePercentage * 12,
    per6: shaftScalePercentage * 16,
    per7: shaftScalePercentage * 20,
    per8: shaftScalePercentage * 36,
  };
  switch (type?.toLowerCase()) {
    case 'pipe_dividers':
      return { width: presetData.per3, height: presetData.per7, length: presetData.per3 };
    case 'pipes':
      return { width: presetData.per2, height: presetData.per7, length: presetData.per2, type: null };
    case 'cables':
      return { width: presetData.per1, height: presetData.per4, length: presetData.per1 };
    case 'skips_and_cages':
      return { width: presetData.per5, height: presetData.perX, length: presetData.per5, type: null };
    case 'wall_brackets':
      return { width: presetData.per3, height: presetData.per3, length: presetData.per4 };
    case 'guides':
      return { width: presetData.per2, height: presetData.per2, length: presetData.per2 };
    case 'brattice_walls':
      return { width: presetData.per4, height: presetData.per4, length: presetData.per6 };
    case 'sidewalls':
      if (isCircle.value) return { width: presetData.per2, height: shaft.value.set_spacing };
      return { width: presetData.per2, height: presetData.per5, length: presetData.per8 };
    default:
      return { width: presetData.per5, height: presetData.per5, length: presetData.per8 };
  }
}

function dismissDialog() {
  dialogIsOpen.value = false;
  deletionId.value = null;
}

async function selectElementToDelete(element) {
  deletionId.value = element.id;
  dialogIsOpen.value = true;
}

async function handleDelete() {
    let { ok } = await ajax(`${ENDPOINTS.value.shaftElements}/${deletionId.value}`,
    { method: 'DELETE' }
  );

  if (ok) {
    dismissDialog();
    clearScene(SCENE);
    loadDefinitionData();
  }
}

function highlightOnHover(id) {
  listHoverState.value = true;
  applyConveyanceStyle(SCENE);

  getAllElements(SCENE).forEach((element) => {
    setElementMaterialType(element, 'ghosted');
    if (element.userData.id === id) {
      setElementMaterialType(element);
    }
  });
}

function resetHighlightHover() {
  listHoverState.value = false;
  getAllElements(SCENE).forEach((element) => {
    setElementMaterialType(element);
  });
}

function editElement(element) {
  isEditingState.value = true;
  selectedElementType.value = element.element_type;

  displaySidewalls(selectedElementType.value === 'sidewalls');

  const typeList = getMemberTypes(element.element_type);
  selectedMemberType.value = typeList?.find(type => type.type === element.geometry?.type);

  const filteredItem = elementGroups.value?.find(item => item.id === element.shaft_element_group_id);
  selectedGroup.value = filteredItem || null;

  SCENE.children.forEach((child) => {
    if (child.userData.id === element.id) {
      currentSelectedElement.value = child;
      currentlyEditing.value = child;
      currentlyEditing.value.start_set = element.start_set;
      currentlyEditing.value.end_set = element.end_set;
    }
  });

  showForm.value = true;
}

/**
 * Gets the selected toolbar option and
 * calls the relevant method to update the view
 */
function selectedAction(event) {
  flattenElements(event);
  event === 'reset' && resetCamera();
}

/**
 * Fetches the pipe or conveyance type list
 */
function getMemberTypes(value) {
  const pipeType = elementTypes.value.find((type) => type.name === 'pipes');

  if (value === 'pipes') return (typeOptions.value = pipeType?.types.map((type) => ({
      name: I18n.t(`shafts.group.type.${type}`),
      type,
    })));

  if (value === 'skips_and_cages') return (typeOptions.value = conveyanceTypeOptions);
}
</script>
<style scoped lang="scss">
@import '@/stylesheets/global';
.element-list {
  margin-top: bu(5);
  padding-top: bu(2);
  border-top: 1px solid map-get($background-color, lighter);

  p {
    margin: bu(1) 0;
  }

  &__group {
    margin-bottom: bu(2.5);
  }

  &__group-section {
    border-radius: map-get($border-radius, heavy);
    overflow: hidden;
  }


  &__group-section-items {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: bu(1);
    background-color: map-get($background-color, lighter);

    &:not(:first-child) {
      border-top: 2px solid map-get($background-color, default)
    }
  }

  &__group-section-items-actions {
    display: flex;
  }


  &__filter {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

$aside-width: rem(355);
$padding: bu(2.25) bu(1.75);

.definition-layout {
  display: grid;
  gap: bu(0.5);
  grid-template-columns: minmax(0, 1fr) $aside-width;
  background-color: map-get($background-color, dark);

  aside {
    background-color: map-get($background-color, default);
    padding: $padding;
    padding-bottom: bu(15);
  }

  &__select-state {
    margin-bottom: bu(2);
  }

  &__select {
    background-color: map-get($background-color, lighter);
    border-radius: map-get($border-radius, heavy);
    padding: 0 bu(1);
  }

  &__form-actions {
    display: flex;
    justify-content: flex-end;
    padding: 0 bu(1);
    margin-top: bu(3);
  }

  &__element-list {
    border-top: 1px solid map-get($background-color, lightest);
    margin-top: bu(2);
    padding-top: bu(2);
  }

  &__element-list-header {
    margin-bottom: bu(2);
  }
}
</style>
